import React, { useState } from "react";
import {
  Drawer,
  Box,
  Typography,
  InputBase,
  IconButton,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import DashboardIcon from "@mui/icons-material/Dashboard";
import CampaignIcon from "@mui/icons-material/Campaign";
import LogoutIcon from "@mui/icons-material/Logout";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import GroupsIcon from "@mui/icons-material/Groups";
import CloseIcon from "@mui/icons-material/Close";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ClassIcon from "@mui/icons-material/Class";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import InsightsIcon from "@mui/icons-material/Insights";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import ApartmentIcon from "@mui/icons-material/Apartment";
import ExtensionIcon from "@mui/icons-material/Extension";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import FolderIcon from "@mui/icons-material/Folder";
import BookIcon from "@mui/icons-material/Book";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import EventNoteIcon from "@mui/icons-material/EventNote";
import SettingsIcon from "@mui/icons-material/Settings";

import { Label } from "recharts";

const Sidebar = ({ mobileOpen, onDrawerToggle, onPanelToggle }) => {
  const [panelOpen, setPanelOpen] = useState(false);
  const drawerWidth = panelOpen ? 320 : 100;
  const storedRole = localStorage.getItem("role") || "admin";
  const [selectedNavGroup, setSelectedNavGroup] = useState("Dashboard");
  const [filteredNav, setFilteredNav] = useState(["Dashboard"]);
  const role = storedRole.toLowerCase();
  const navigate = useNavigate();
  const location = useLocation();
  React.useEffect(() => {
    const active = menuItems.find((item) => item.path === location.pathname);
    if (active) setSelectedNav(active.label);
  }, [location.pathname]);
  const navGroups = {
    Dashboard: ["Dashboard"],
    Students: ["Student Management"],
    Teachers: ["Teacher Management"],
    Timetable: ["Timetable Management", "Timetable"],
    Fees: ["Fee Management"],
    Activity: ["Activity", "Progress Tracking"],
    Branches: ["Branch Management"],
    Announcements: ["Announcements"],
    Profile: ["Profile"],
  };

  const adminMenu = [
    { label: "Dashboard", icon: <DashboardIcon />, path: "/admin" },
    {
      label: "Student Management",
      icon: <GroupsIcon />,
      path: "/admin/student-listing",
    },
    {
      label: "Teacher Management",
      icon: <PersonOutlineIcon />,
      path: "/admin/staff-page",
    },
    { label: "Class Management", icon: <ClassIcon />, path: "/admin/class" },
    {
      label: "Payment Management",
      icon: <AccountBalanceWalletIcon />,
      path: "/admin/fees",
    },
    {
      label: "Progress Tracking",
      icon: <InsightsIcon />,
      path: "/admin/progress",
    },
    {
      label: "Staff Attendance",
      icon: <HowToRegIcon />,
      path: "/admin/staff-viewattendance",
    },
    {
      label: "Student Attendance",
      icon: <HowToRegIcon />,
      path: "/teacher/attendance",
    },
    {
      label: "Timetable Management",
      icon: <CalendarMonthIcon />,
      path: "/admin/timetable",
    },
    {
      label: "Event Calendar",
      icon: <EventAvailableIcon />,
      path: "/admin/calender",
    },
    {
      label: "Announcements",
      icon: <CampaignIcon />,
      path: "/admin/announcement",
    },
    {
      label: "Enquiries",
      icon: <ContactMailIcon />,
      path: "/admin/enquiries",
    },
    {
      label: "Schedule Visits",
      icon: <EventNoteIcon />,
      path: "/admin/schedule-visits",
    },
    {
      label: "Student Diary",
      icon: <BookIcon />,
      path: "/admin/diary",
    },
    {
      label: "Branch Management",
      icon: <ApartmentIcon />,
      path: "/admin/AdminBranches",
    },
    { label: "Activity", icon: <ExtensionIcon />, path: "/admin/activity" },
    {
      label: "Upload Data",
      icon: <CloudUploadIcon />,
      path: "/admin/upload-data",
    },
    {
      label: "View Files",
      icon: <FolderIcon />,
      path: "/admin/view-data",
    },
    {
      label: "Settings",
      icon: <SettingsIcon />,
      path: "/admin/settings",
    },
    {
      label: "Profile",
      icon: <AccountCircleIcon />,
      path: "/admin/admin-profile",
    },
  ];

  const teacherMenu = [
    { label: "Dashboard", icon: <DashboardIcon />, path: "/teacher" },
    {
      label: "Attendance",
      icon: <HowToRegIcon />,
      path: "/teacher/attendance",
    },
    {
      label: "Activities",
      icon: <ExtensionIcon />,
      path: "/teacher/activity",
    },
    {
      label: "Progress Tracking",
      icon: <InsightsIcon />,
      path: "/teacher/progress",
    },
    {
      label: "Timetable",
      icon: <CalendarMonthIcon />,
      path: "/teacher/timetable",
    },
    {
      label: "Announcements",
      icon: <CampaignIcon />,
      path: "/teacher/announcement",
    },
    {
      label: "Student Diary",
      icon: <BookIcon />,
      path: "/teacher/diary",
    },
    {
      label: "Event Calendar",
      icon: <EventAvailableIcon />,
      path: "/teacher/calender",
    },
    {
      label: "Profile",
      icon: <AccountCircleIcon />,
      path: "/teacher/staff-profile",
    },
  ];

  const menuItems = role === "admin" ? adminMenu : teacherMenu;

  const [selectedNav, setSelectedNav] = useState(menuItems[0]?.label || "");

  const handleNavClick = (label) => {
    setSelectedNav(label);
  };

  const handlePanelIconClick = () => {
    const newPanelState = !panelOpen;
    setPanelOpen(newPanelState);
    if (onPanelToggle) {
      onPanelToggle(newPanelState);
    }
  };

  const handleDrawerClose = () => {
    if (onDrawerToggle) onDrawerToggle();
    setPanelOpen(false);
    if (onPanelToggle) onPanelToggle(false);
  };

  const handleLogout = () => {
    // Clear auth storage
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    localStorage.removeItem("user"); // if you store user info

    // Close sidebar states
    setPanelOpen(false);
    if (onPanelToggle) onPanelToggle(false);
    if (onDrawerToggle) onDrawerToggle(false);

    // Navigate to login
    navigate("/", { replace: true });

    // Optional: hard reload (recommended for safety)
    window.location.reload();
  };

  const drawerContent = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "row",
        p: { xs: 1.5, sm: 2 },
        pr: 0,
        alignItems: "stretch",
        maxWidth: "100%",
        overflow: "hidden",
      }}
    >
      {/* LEFT ICON STRIP */}
      <Box
        sx={{
          width: { xs: 52, sm: 56 },
          overflow: "auto",
          borderRadius: 999,
          bgcolor: "#ffffff",
          boxShadow: "0 18px 40px rgba(15,23,42,0.25)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          py: { xs: 1.5, sm: 2 },
          gap: 1.8,
          height: "100%",
          flexShrink: 0,
        }}
      >
        {menuItems.map((item) => {
          const isActive = selectedNav === item.label;

          return (
            <IconButton
              key={item.label}
              onClick={() => {
                setSelectedNav(item.label);
                navigate(item.path);

                // 🔥 OPEN DARK PANEL WHEN ANY ICON CLICKED
                setPanelOpen(true);
                if (onPanelToggle) onPanelToggle(true);

                // ❌ REMOVE THIS PART IF YOU want sidebar to stay open:
                // if (onDrawerToggle) onDrawerToggle(false);
              }}
              sx={{
                width: 40,
                height: 40,
                borderRadius: "12px",
                bgcolor: isActive ? "rgba(99,102,241,0.18)" : "transparent",
                boxShadow: isActive ? "0 0 10px rgba(99,102,241,0.7)" : "none",
                transition: "all .25s ease",
                color: isActive ? "#4F46E5" : "#6B7280",
                "&:hover": {
                  bgcolor: "rgba(99,102,241,0.14)",
                  transform: "scale(1.05)",
                },
              }}
            >
              {item.icon}
            </IconButton>
          );
        })}

        <Box sx={{ flexGrow: 1 }} />

        {/* <IconButton
          size="small"
          onClick={handleDrawerClose}
          sx={{
            width: 32,
            height: 32,
            borderRadius: "10px",
            color: "#fff",
            bgcolor: "#111827",
            "&:hover": { bgcolor: "rgba(15,23,42,1)" },
          }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton> */}
      </Box>

      {/* MAIN DARK PANEL – dark background + white text */}
      <Box
        sx={{
          ml: panelOpen ? { xs: 1.5, sm: 2 } : 0,
          flexGrow: panelOpen ? 1 : 0,
          width: panelOpen ? "auto" : 0,
          opacity: panelOpen ? 1 : 0,
          pointerEvents: panelOpen ? "auto" : "none",
          width: panelOpen ? { xs: 240, md: 320 } : 0, // FIXED WIDTH
          transform: panelOpen ? "translateX(0)" : "translateX(-16px)",
          transition: "all 0.25s ease, opacity 0.2s ease, transform 0.25s ease",
          borderRadius: { xs: 3, sm: 4 },
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          // boxShadow: panelOpen ? "0 24px 80px rgba(15,23,42,0.65)" : "none",
          background:
            "radial-gradient(120% 160% at 0% 0%, #111827 0%, #020617 45%, #020617 100%)",
          color: "#F9FAFB",
          alignSelf: "stretch",
          minWidth: 0,
        }}
      >
        {panelOpen && (
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
              px: { xs: 2, sm: 2.5 },
              pt: { xs: 2, sm: 2.5 },
              pb: 2,
              boxSizing: "border-box",
              height: "100%",
              minWidth: 0,
            }}
          >
            {/* HEADER */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 3,
                pb: 1.5,
                borderBottom: "1px solid rgba(148,163,184,0.18)",
                gap: 1,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: "18px", sm: "20px" },
                    letterSpacing: "0.5px",
                    background:
                      "linear-gradient(90deg, #FFFFFF 0%, #94A3B8 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                  }}
                >
                  {selectedNav}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexShrink: 0,
                }}
              >
                <IconButton
                  size="small"
                  onClick={() => {
                    setPanelOpen(false);
                    if (onPanelToggle) onPanelToggle(false);
                    if (onDrawerToggle) onDrawerToggle(false); // CLOSE FULL SIDEBAR
                  }}
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "999px",
                    bgcolor: "rgba(15,23,42,0.9)",
                    color: "#F9FAFB",
                    "&:hover": { bgcolor: "rgba(15,23,42,1)" },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            </Box>

            {/* NAVIGATION */}
            <Box sx={{ flexGrow: 1, minHeight: 0 }}>
              <List
                dense
                disablePadding
                sx={{
                  maxHeight: "100%",
                  overflowY: "auto",
                  pr: 0.5,
                  "&::-webkit-scrollbar": {
                    width: 0,
                    height: 0,
                  },
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                }}
              >
                {menuItems.map((item) => (
                  <ListItemButton
                    key={item.label}
                    onClick={() => {
                      setSelectedNav(item.label);
                      navigate(item.path);
                      setPanelOpen(false);
                      if (onPanelToggle) onPanelToggle(false);
                      if (onDrawerToggle) onDrawerToggle(false);
                    }}
                    selected={selectedNav === item.label}
                    sx={{
                      borderRadius: "10px",
                      px: 2,
                      py: 1,
                      mb: 1,
                      transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                      "& .MuiListItemIcon-root": {
                        minWidth: 36,
                      },
                      "&.Mui-selected": {
                        bgcolor: "rgba(99,102,241,0.22)",
                        borderLeft: "3px solid #818CF8",
                        transform: "translateX(2px)",
                        "& .MuiListItemIcon-root": {
                          color: "#818CF8",
                        },
                        "& .MuiListItemText-primary": {
                          fontWeight: 600,
                          color: "#FFFFFF",
                        },
                      },
                      "&.Mui-selected:hover": {
                        bgcolor: "rgba(99,102,241,0.3)",
                      },
                      "&:hover": {
                        bgcolor: "rgba(148,163,184,0.16)",
                        transform: "translateX(4px)",
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        color:
                          selectedNav === item.label ? "#818CF8" : "#9CA3AF",
                        transition: "color 0.2s ease",
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontSize: 14,
                        fontWeight: selectedNav === item.label ? 600 : 400,
                        letterSpacing: "0.3px",
                        color:
                          selectedNav === item.label ? "#FFFFFF" : "#E5E7EB",
                      }}
                    />
                    {selectedNav === item.label && (
                      <KeyboardArrowRightIcon
                        sx={{
                          fontSize: 16,
                          color: "#818CF8",
                          ml: 1,
                        }}
                      />
                    )}
                  </ListItemButton>
                ))}

                <Divider
                  sx={{
                    my: 3,
                    borderColor: "rgba(148,163,184,0.18)",
                  }}
                />
              </List>
            </Box>

            <Box
              sx={{
                mt: "auto",
                pt: 2,
                borderTop: "1px solid rgba(148,163,184,0.18)",
              }}
            >
              <ListItemButton
                onClick={handleLogout}
                sx={{
                  borderRadius: "10px",
                  px: 2,
                  py: 1.2,
                  bgcolor: "rgba(239,68,68,0.08)",
                  border: "1px solid rgba(239,68,68,0.2)",
                  transition: "all 0.25s ease",
                  "&:hover": {
                    bgcolor: "rgba(239,68,68,0.16)",
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 12px rgba(239,68,68,0.18)",
                  },
                }}
              >
                <ListItemIcon sx={{ color: "#FCA5A5" }}>
                  <LogoutIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Logout"
                  primaryTypographyProps={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: "#FCA5A5",
                    letterSpacing: "0.3px",
                  }}
                />
              </ListItemButton>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <>
      {/* Mobile drawer */}
      <Drawer
        anchor="left"
        variant="temporary"
        open={mobileOpen}
        onClose={(_, reason) => {
          // close when clicking outside or pressing Esc
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            if (onDrawerToggle) onDrawerToggle(false); // close the drawer
            setPanelOpen(false); // also close dark panel
          }
        }}
        ModalProps={{
          keepMounted: true,
          disableScrollLock: true, // ✅ ADD HERE
        }}
        sx={{
          display: { xs: "block", md: "none" },
        }}
        PaperProps={{
          sx: {
            boxSizing: "border-box",
            width: "auto",
            border: "none",
            bgcolor: "transparent",
            overflow: "visible",
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop drawer */}
      <Drawer
        variant="permanent"
        open
        ModalProps={{
          disableScrollLock: true, // ✅ ADD HERE (safe even if ignored)
        }}
        sx={{
          display: { xs: "none", md: "block" },
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: drawerWidth,
            maxWidth: drawerWidth,
            border: "none",
            bgcolor: "transparent",
            overflow: "hidden",
          },
        }}
      >
        {drawerContent}
      </Drawer>
    </>
  );
};

export default Sidebar;
